import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Dialog, DialogActions, DialogContent, DialogTitle, FormControl, FormHelperText, Grid, LinearProgress, MenuItem, Select, SelectProps, Typography, Box, Paper, IconButton, Button } from '@mui/material';
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { CloudUpload } from '@mui/icons-material';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import { DataGridPro, GridColDef } from '@mui/x-data-grid-pro';
import { FieldAttributes, Form, Formik, useField } from 'formik';
import Swal from 'sweetalert2';
import useAuth from '../../context/AuthProvider';
import { apiClient } from '../../helper/api';
import { stateCodeToDocumentType, STAMPED_STATE_CODES } from '../../helper/stampedDocumentState';
import { getRowsPerPageOptions } from './CustomFunctions';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';

const ALLOWED_FILE_TYPES = ['.pdf', '.doc', '.docx', '.xls', '.xlsx'] as const;
const FILE_ACCEPT_ATTRIBUTE = '.pdf,.doc,.docx,.xls,.xlsx';

interface Iprops {
    handleClose: () => void;
    Open: boolean;
    onSuccess?: () => void;
    onLoadingChange?: (loading: boolean) => void;
}

interface Partner {
    partnersId: number;
    customerName: string;
}

interface UploadedFile {
    id: string;
    file: File;
    name: string;
    size: number;
}

const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'] as const;
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
};

const createFileObject = (file: File): UploadedFile => ({
    id: Math.random().toString(36).substr(2, 9),
    file,
    name: file.name,
    size: file.size,
});

function isPdfFileName(name: string): boolean {
    return name.toLowerCase().endsWith('.pdf');
}

function validateStampedForm(partnersId: number, stateCode: string, files: UploadedFile[]): string | null {
    if (!partnersId || partnersId === 0) return 'Please select a client';
    if (!stateCode || !stateCodeToDocumentType(stateCode)) return 'Please select a state (NY or IL)';
    if (!files || files.length === 0) return 'Please select at least one file to upload';
    return null;
}

export const AddStampedDocument: React.FC<Iprops> = ({ handleClose, Open, onSuccess, onLoadingChange }) => {
    const { user } = useAuth();
    const token = user?.token;
    const [loading, setLoadingState] = useState(false);
    const setLoading = (value: boolean) => {
        setLoadingState(value);
        onLoadingChange?.(value);
    };
    const [formData, setFormData] = useState({
        partnersId: 0,
        documentType: '',
    });
    const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
    const [isDragOver, setIsDragOver] = useState(false);
    const [pageSize, setPageSize] = useState(10);
    const [currentPage, setCurrentPage] = useState(0);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [fileViewerOpen, setFileViewerOpen] = useState(false);
    const [fileViewerTitle, setFileViewerTitle] = useState('');
    const [fileViewerUrl, setFileViewerUrl] = useState<string | null>(null);
    const fileViewerBlobUrlRef = useRef<string | null>(null);

    const initialPartnerData: Partner = { partnersId: 0, customerName: "" };
    const [partners, setPartners] = useState<Partner[]>([initialPartnerData]);
    
    const fetchPartnersData = () => {
        apiClient('/Partners/getPartners', {
            method: "GET",
            headers: new Headers({
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
            })
        }).then((responseJson) => {
            if (!responseJson) return;
            setPartners(responseJson.data);
        }).catch(error => {
            console.error("Error fetching partners:", error)
        });
    };

    const closeFileViewer = useCallback(() => {
        if (fileViewerBlobUrlRef.current) {
            URL.revokeObjectURL(fileViewerBlobUrlRef.current);
            fileViewerBlobUrlRef.current = null;
        }
        setFileViewerUrl(null);
        setFileViewerTitle('');
        setFileViewerOpen(false);
    }, []);

    const closeDialogFully = () => {
        closeFileViewer();
        handleClose();
    };

    useEffect(() => {
        if (!Open) {
            closeFileViewer();
            return;
        }
        fetchPartnersData();
        setFormData({
            partnersId: 0,
            documentType: '',
        });
        setUploadedFiles([]);
        setIsDragOver(false);
        closeFileViewer();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- open-only reset
    }, [Open, closeFileViewer]);

    useEffect(() => {
        return () => {
            if (fileViewerBlobUrlRef.current) {
                URL.revokeObjectURL(fileViewerBlobUrlRef.current);
                fileViewerBlobUrlRef.current = null;
            }
        };
    }, []);

    const handleFileSelect = (files: FileList | null) => {
        if (!files) return;
        
        const invalidFiles: string[] = [];
        
        // Check file types
        Array.from(files).forEach(file => {
            const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();
            if (!ALLOWED_FILE_TYPES.includes(fileExtension as any)) {
                invalidFiles.push(file.name);
            }
        });
        
        // Show error if invalid files found
        if (invalidFiles.length > 0) {
            Swal.fire({
                title: 'Invalid File Type',
                text: `Only PDF, DOC, DOCX, XLS, and XLSX files are allowed. Invalid files: ${invalidFiles.join(', ')}`,
                icon: 'error',
                confirmButtonColor: '#02A4E3'
            });
            return;
        }
        
        const newFiles = Array.from(files).map(createFileObject);
        setUploadedFiles(prev => [...prev, ...newFiles]);

        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const removeFile = (fileId: string) => {
        setUploadedFiles(prev => prev.filter(file => file.id !== fileId));
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const openFileViewer = useCallback((fileId: string) => {
        const entry = uploadedFiles.find((f) => f.id === fileId);
        if (!entry) return;
        if (!isPdfFileName(entry.name)) {
            Swal.fire({
                title: 'Preview not available',
                text: 'Inline preview is not available for Word or Excel files.',
                icon: 'info',
                confirmButtonColor: '#02A4E3',
            });
            return;
        }
        if (fileViewerBlobUrlRef.current) {
            URL.revokeObjectURL(fileViewerBlobUrlRef.current);
            fileViewerBlobUrlRef.current = null;
        }
        const url = URL.createObjectURL(entry.file);
        fileViewerBlobUrlRef.current = url;
        setFileViewerUrl(url);
        setFileViewerTitle(entry.name);
        setFileViewerOpen(true);
    }, [uploadedFiles]);

    const uploadedFilesColumns: GridColDef[] = useMemo(() => [
        {
            field: 'fileName',
            headerName: 'File Name',
            flex: 1,
            minWidth: 200,
            renderCell: (params) => (
                <Typography sx={{ 
                    overflow: 'initial', 
                    whiteSpace: 'break-spaces',
                    lineHeight: 'normal',
                    color: '#162E3C',
                }}>
                    {params.value || '-'}
                </Typography>
            ),
        },
        {
            field: 'sizeText',
            headerName: 'File Size',
            flex: 0.6,
            minWidth: 120,
        },
        {
            field: 'actions',
            headerName: 'Actions',
            sortable: false,
            filterable: false,
            disableColumnMenu: true,
            flex: 0.5,
            minWidth: 140,
            renderCell: (params) => (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <IconButton
                        size="small"
                        aria-label="View file"
                        onClick={() => openFileViewer(params.row.id as string)}
                        sx={{ color: '#162E3C' }}
                    >
                        <VisibilityOutlinedIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                        color="error"
                        aria-label="Remove file"
                        onClick={() => removeFile(params.row.id)}
                    >
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M3 6H5M5 6H21M5 6V20C5 20.5304 5.21071 21.0391 5.58579 21.4142C5.96086 21.7893 6.46957 22 7 22H17C17.5304 22 18.0391 21.7893 18.4142 21.4142C18.7893 21.0391 19 20.5304 19 20V6H5ZM8 6V4C8 3.46957 8.21071 2.96086 8.58579 2.58579C8.96086 2.21071 9.46957 2 10 2H14C14.5304 2 15.0391 2.21071 15.4142 2.58579C15.7893 2.96086 16 3.46957 16 4V6" stroke="#162E3C" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                    </IconButton>
                </Box>
            ),
        },
    ], [removeFile, openFileViewer]);

    const dataGridRows = useMemo(() => 
        uploadedFiles.map((fileObj) => ({
            id: fileObj.id,
            fileName: fileObj.name,
            sizeText: formatFileSize(fileObj.size),
        })),
        [uploadedFiles]
    );

    const FMSelectPartnersCustom: React.FC<FieldAttributes<SelectProps>> = ({ variant, ...props }) => {
        const [field, meta, helpers] = useField<{}>(props);

        return (
            <FormControl fullWidth>
                <FormHelperText className='formtexthelper-heading'>Select Client</FormHelperText>
                <Box sx={{ position: 'relative', width: '100%' }}>
                <Select
                    {...field}
                    labelId="Client-Select-Label"
                    className="border-round"
                    IconComponent={ExpandMoreIcon}
                    label={null}
                    displayEmpty
                    sx={{
                        width: '100%',
                        '& .MuiSelect-select': { padding: '14.5px 16px' },
                        '& .MuiOutlinedInput-notchedOutline': { border: '2px solid #e2e8f0 !important', borderRadius: '8px', padding: '16px' },
                        '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(0, 0, 0, 0.87) !important' },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#1976d2 !important' }
                    }}
                    renderValue={(selected: unknown) => {
                        if (!selected || selected === 0) {
                            return <span style={{ color: '#999' }}>Select Client</span>;
                        }
                        const partner = partners.find(p => p.partnersId === selected);
                        return <span>{partner ? partner.customerName : ''}</span>;
                    }}
                >
                    <MenuItem value={0} onClick={() => helpers.setValue(0)}>
                        Select Client
                    </MenuItem>
                    {partners.filter(p => p.partnersId !== 0).map((partner, key) => (
                        <MenuItem key={key} value={partner.partnersId}>
                            {partner.customerName}
                        </MenuItem>
                    ))}
                </Select>
                </Box>
            </FormControl>
        );
    };

    const FMSelectTypeCustom: React.FC<FieldAttributes<SelectProps>> = ({ variant, ...props }) => {
        const [field, meta, helpers] = useField<{}>(props);

        return (
            <FormControl fullWidth>
                <FormHelperText className='formtexthelper-heading'>Select State</FormHelperText>
                <Box sx={{ position: 'relative', width: '100%' }}>
                    <Select
                        {...field}
                        labelId="State-Select-Label"
                        className="border-round"
                        IconComponent={ExpandMoreIcon}
                        label={null}
                        displayEmpty
                        sx={{
                            width: '100%',
                            '& .MuiSelect-select': { padding: '14.5px 16px' },
                            '& .MuiOutlinedInput-notchedOutline': { border: '2px solid #e2e8f0 !important', borderRadius: '8px', padding: '16px' },
                            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(0, 0, 0, 0.87) !important' },
                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#1976d2 !important' }
                        }}
                        renderValue={(selected: unknown) => {
                            if (!selected || selected === '') {
                                return <span style={{ color: '#999' }}>Select State</span>;
                            }
                            return <span>{selected as string}</span>;
                        }}
                    >
                        <MenuItem value="" onClick={() => helpers.setValue('')}>
                            Select State
                        </MenuItem>
                        {STAMPED_STATE_CODES.map((code) => (
                            <MenuItem key={code} value={code}>{code}</MenuItem>
                        ))}
                    </Select>
                </Box>
            </FormControl>
        );
    };


    return (
        <>
        <Dialog
            open={Open}
            onClose={closeDialogFully}
            maxWidth="lg"
            fullWidth
            PaperProps={{
                sx: {
                    width: '90%',
                    maxWidth: 900,
                    minHeight: '70vh',
                    maxHeight: '90vh',
                    borderRadius: 2,
                    boxShadow: '0 24px 48px rgba(0,0,0,0.12)',
                },
            }}
            sx={{ '& .MuiDialog-paper': { overflow: 'hidden' } }}
        >
            {loading && <LinearProgress sx={{ flexShrink: 0 }} />}
            <DialogTitle
                sx={{
                    position: 'sticky',
                    top: 0,
                    zIndex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 1,
                    py: 2,
                    px: 3,
                    background: 'linear-gradient(135deg, #0d4a63 0%, #15607a 100%)',
                    color: '#fff',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <DescriptionOutlinedIcon sx={{ fontSize: 28, opacity: 0.95 }} />
                    <Typography component="span" variant="h6" sx={{ fontWeight: 600, letterSpacing: '0.02em' }}>
                        I Want To... Send a Stamped Document
                    </Typography>
                </Box>
                <IconButton
                    onClick={closeDialogFully}
                    sx={{ color: 'rgba(255,255,255,0.9)', '&:hover': { color: '#fff', backgroundColor: 'rgba(255,255,255,0.12)' } }}
                    aria-label="Close dialog"
                >
                    <CloseOutlinedIcon />
                </IconButton>
            </DialogTitle>
            <Formik 
                initialValues={formData}
                enableReinitialize={true}
                validateOnChange={true}
                onSubmit={() => {}}
            >
                {({ values, isSubmitting, setFieldValue, errors }) => {
                    const clientDisplayName =
                        partners.find((partner) => partner.partnersId === values.partnersId)?.customerName || 'the selected client';

                    const confirmSendStampedDocument = () => {
                        Swal.fire({
                            title: '<div style="color: white;">Are you sure?</div>',
                            html: `<div style="color: white;">This will send a stamped document to the Client Portal for <span style="color: yellow;">${clientDisplayName}</span></div>`,
                            showCancelButton: true,
                            confirmButtonText: 'Yes, Proceed',
                            cancelButtonText: 'No, Continue to EDIT',
                            confirmButtonColor: '#4caf50',
                            cancelButtonColor: '#ff7b00',
                            reverseButtons: true,
                            background: '#0d4a63',
                        }).then(async (result) => {
                            if (!result.isConfirmed) return;
                            setLoading(true);
                            try {
                                const fd = new FormData();
                                fd.append(
                                    'data',
                                    JSON.stringify({
                                        partnersId: values.partnersId,
                                        documentType: stateCodeToDocumentType(values.documentType),
                                    })
                                );
                                uploadedFiles.forEach((fileObj, index) => {
                                    fd.append(`file_${index}`, fileObj.file);
                                    fd.append(`fileName_${index}`, fileObj.name);
                                });
                                fd.append('fileCount', uploadedFiles.length.toString());

                                const response = await apiClient('/StampedDocument/sendToClient', {
                                    method: 'POST',
                                    headers: new Headers({
                                        Authorization: `Bearer ${token}`,
                                    }),
                                    body: fd,
                                });

                                if (response?.status === true) {
                                    Swal.fire({
                                        title: 'Success',
                                        text: response.message || 'The stamped document has been sent to the client.',
                                        icon: 'success',
                                        confirmButtonText: 'OK',
                                        confirmButtonColor: '#02A4E3',
                                    });
                                    setUploadedFiles([]);
                                    handleClose();
                                    onSuccess?.();
                                } else {
                                    Swal.fire({
                                        title: 'Error',
                                        text: response?.message || 'Failed to send stamped document to client.',
                                        icon: 'error',
                                        confirmButtonText: 'OK',
                                    });
                                }
                            } catch (error) {
                                console.error('Error sending stamped document to client:', error);
                                Swal.fire({
                                    title: 'Error',
                                    text: 'Failed to send stamped document to client. Please try again.',
                                    icon: 'error',
                                    confirmButtonText: 'OK',
                                });
                            } finally {
                                setLoading(false);
                            }
                        });
                    };

                    return (
                        <Form className='main-form'>
                            <DialogContent sx={{ width: '100%', minHeight: '50vh', overflow: 'auto', px: { xs: 2, sm: 3 }, py: 3 }}>
                                <Grid container spacing={3}>
                                    {/* Section: Client & document type */}
                                    <Grid item xs={12}>
                                        <Typography variant="overline" sx={{ color: 'text.secondary', fontWeight: 600, letterSpacing: '0.08em', display: 'block', mb: 1 }}>
                                            Client & state
                                        </Typography>
                                        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, borderColor: 'divider', backgroundColor: 'grey.50' }}>
                                            <Grid container spacing={2}>
                                                <Grid item xs={12} md={6}>
                                                    <FMSelectPartnersCustom name="partnersId" />
                                                </Grid>
                                                <Grid item xs={12} md={6}>
                                                    <FMSelectTypeCustom name="documentType" />
                                                    <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '12px', mt: 0.5, ml: 0.5 }}>
                                                        NY or IL (stamped document program)
                                                    </Typography>
                                                </Grid>
                                            </Grid>
                                        </Paper>
                                    </Grid>

                                    {/* Section: Attachments */}
                                    <Grid item xs={12}>
                                        <Typography variant="overline" sx={{ color: 'text.secondary', fontWeight: 600, letterSpacing: '0.08em', display: 'block', mb: 1 }}>
                                            Attachments
                                        </Typography>
                                        <FormHelperText className='formtexthelper-heading' sx={{ mb: 0.5 }}>Select file(s) or drag and drop</FormHelperText>
                                        <Paper
                                            sx={{
                                                border: isDragOver ? '2px dashed #02A4E3' : '1px dashed #ccc',
                                                borderRadius: 2,
                                                padding: 4,
                                                textAlign: 'center',
                                                backgroundColor: isDragOver ? '#f0f8ff' : '#fafafa',
                                                cursor: 'pointer',
                                                transition: 'all 0.3s ease',
                                                mb: 3,
                                                minHeight: 200,
                                                width: '100%',
                                                display: 'flex',
                                                flexDirection: 'column',
                                                justifyContent: 'center',
                                                alignItems: 'center',
                                                boxShadow: 'none'
                                            }}
                                            onDragOver={(event) => {
                                                event.preventDefault();
                                                setIsDragOver(true);
                                            }}
                                            onDragLeave={(event) => {
                                                event.preventDefault();
                                                setIsDragOver(false);
                                            }}
                                            onDrop={(event) => {
                                                event.preventDefault();
                                                setIsDragOver(false);
                                                handleFileSelect(event.dataTransfer.files);
                                            }}
                                            onClick={() => fileInputRef.current?.click()}
                                        >
                                            <CloudUpload sx={{ fontSize: 48, color: '#02A4E3', mb: 2 }} />
                                            <Typography variant="h6" sx={{ color: '#666', mb: 1 }}>
                                                Select File or Drag and Drop
                                            </Typography>
                                            <Typography variant="body2" sx={{ color: '#999' }}>
                                                Click to browse files or drag and drop multiple files here
                                            </Typography>
                                            <input
                                                ref={fileInputRef}
                                                type="file"
                                                multiple
                                                accept={FILE_ACCEPT_ATTRIBUTE}
                                                style={{ display: 'none' }}
                                                onChange={(e) => handleFileSelect(e.target.files)}
                                            />
                                        </Paper>

                                        {/* Uploaded Files DataGrid */}
                                        {uploadedFiles.length > 0 && (
                                            <Box sx={{ mt: 2, mb: 0 }}>
                                                <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600, color: 'text.secondary' }}>
                                                    Attached documents ({uploadedFiles.length})
                                                </Typography>
                                                <Box sx={{ width: '100%', mb: 2 }}>
                                                    <DataGridPro
                                                        autoHeight
                                                        rows={dataGridRows}
                                                        columns={uploadedFilesColumns}
                                                        disableSelectionOnClick
                                                        disableColumnMenu
                                                        pageSize={pageSize}
                                                        pagination
                                                        rowCount={uploadedFiles.length}
                                                        paginationMode="client"
                                                        onPageChange={setCurrentPage}
                                                        onPageSizeChange={(newPageSize) => {
                                                            setPageSize(newPageSize);
                                                            setCurrentPage(0);
                                                        }}
                                                        rowsPerPageOptions={getRowsPerPageOptions(uploadedFiles.length, pageSize)}
                                                        sx={{
                                                            borderRadius: '10px',
                                                            overflow: 'hidden',
                                                            fontFamily: "'Proxima Nova', sans-serif",
                                                            fontSize: '16px',
                                                            fontWeight: 'normal',
                                                            '& .MuiDataGrid-main': {
                                                                overflow: 'hidden'
                                                            },
                                                            '& .MuiDataGrid-columnHeaderTitle': {
                                                                whiteSpace: 'normal',
                                                                lineHeight: 'normal',
                                                            },
                                                            border: '0px'
                                                        }}
                                                    />
                                                </Box>
                                            </Box>
                                        )}
                                    </Grid>
                                </Grid>
                            </DialogContent>
                            <DialogActions
                                sx={{
                                    px: 3,
                                    py: 2,
                                    gap: 1.5,
                                    borderTop: 1,
                                    borderColor: 'divider',
                                    backgroundColor: 'grey.50',
                                    flexWrap: 'wrap',
                                }}
                            >
                                <Button
                                    variant="outlined"
                                    size="medium"
                                    type="button"
                                    onClick={closeDialogFully}
                                    startIcon={<CloseOutlinedIcon />}
                                    sx={{
                                        textTransform: 'none',
                                        fontWeight: 600,
                                        borderColor: '#e2e8f0',
                                        color: '#475569',
                                        '&:hover': {
                                            borderColor: '#2099d2',
                                            backgroundColor: '#2099d214',
                                            color: '#2099d2',
                                        },
                                    }}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    variant="contained"
                                    size="medium"
                                    type="button"
                                    disabled={isSubmitting || loading}
                                    onClick={() => {
                                        const err = validateStampedForm(
                                            values.partnersId,
                                            values.documentType,
                                            uploadedFiles
                                        );
                                        if (err) {
                                            Swal.fire({
                                                title: 'Validation Error',
                                                text: err,
                                                icon: 'warning',
                                                confirmButtonText: 'OK',
                                            });
                                            return;
                                        }
                                        confirmSendStampedDocument();
                                    }}
                                    startIcon={<SendOutlinedIcon />}
                                    sx={{
                                        textTransform: 'none',
                                        fontWeight: 600,
                                        backgroundColor: '#2099d2',
                                        '&:hover': { backgroundColor: '#176990' },
                                        px: 2.5,
                                    }}
                                >
                                    Send to Client
                                </Button>
                            </DialogActions>
                        </Form>
                    );
                }}
            </Formik>
        </Dialog>
        <Dialog
            open={fileViewerOpen}
            onClose={closeFileViewer}
            maxWidth="lg"
            fullWidth
            PaperProps={{
                sx: {
                    height: '90vh',
                    maxHeight: 900,
                },
            }}
        >
            <DialogTitle
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 1,
                    py: 1.5,
                    px: 2,
                    borderBottom: 1,
                    borderColor: 'divider',
                }}
            >
                <Typography variant="subtitle1" sx={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {fileViewerTitle}
                </Typography>
                <IconButton onClick={closeFileViewer} aria-label="Close preview" size="small">
                    <CloseOutlinedIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent sx={{ p: 0, height: 'calc(90vh - 57px)', maxHeight: 843 }}>
                {fileViewerUrl ? (
                    <Box
                        component="iframe"
                        title={fileViewerTitle}
                        src={fileViewerUrl}
                        sx={{
                            width: '100%',
                            height: '100%',
                            minHeight: 400,
                            border: 'none',
                            display: 'block',
                        }}
                    />
                ) : null}
            </DialogContent>
        </Dialog>
        </>
    );
};

export default AddStampedDocument;

